/**
 * The briefing — the home screen (SPEC.md §8.2).
 *
 * Just the page shell: every section on it is portable now, including manual entry, the
 * dismissable notice and the first-run prompt (`QuickAddCard`/`NoticeCard`/`FirstRunCard`,
 * D118) — so this only has to ask `sectionsForPage` what belongs here and render each through
 * the shared registry, the same reduction `Structure.tsx`/`Settings.tsx` went through
 * (D110). See DECISIONS.md D104-D110 and D118 for how each card got here.
 */

import { useCallback, useState } from "react";
import { sectionsForPage, type SectionId } from "../layout/model";
import { renderSection } from "../layout/sectionRegistry";
import { LayoutGrid, type Section } from "../layout/LayoutGrid";
import { useQueueDelivered } from "../useQueueDelivered";

export function Briefing() {
  const [revision, setRevision] = useState(0);
  const bump = useCallback(() => setRevision((n) => n + 1), []);
  // A queued change landing is a change to this page's data like any other (D160).
  useQueueDelivered(bump);

  const sections: Section[] = sectionsForPage("briefing")
    .map((id): Section | null => {
      const node = renderSection(id as SectionId, { refreshKey: revision, onChange: bump });
      return node ? { id, node } : null;
    })
    .filter((section): section is Section => section !== null);

  return <LayoutGrid page="briefing" sections={sections} onSectionsChanged={bump} />;
}
